import { cn } from "~/lib/utils";

interface TypographyProps {
  children: React.ReactNode;
  className?: string;
}

type H1Variant = "marketing" | "content" | "product";

interface TypographyH1Props extends TypographyProps {
  variant?: H1Variant;
}

const h1VariantClasses: Record<H1Variant, string> = {
  marketing: "h1-marketing",
  content: "h1-content",
  product: "h1-product",
};

export function TypographyH1({
  variant,
  className,
  children,
}: TypographyH1Props) {
  return (
    <h1
      className={cn(
        "scroll-m-20",
        variant
          ? h1VariantClasses[variant]
          : "text-4xl font-semibold tracking-tight lg:text-5xl",
        className,
      )}
    >
      {children}
    </h1>
  );
}

export function TypographyH2(props: TypographyProps) {
  return (
    <h2
      className={cn(
        "scroll-m-20 pb-2 text-3xl font-semibold tracking-tight first:mt-0",
        props.className,
      )}
    >
      {props.children}
    </h2>
  );
}

export function TypographyH3(props: TypographyProps) {
  return (
    <h3
      className={cn(
        "scroll-m-20 text-2xl font-semibold tracking-tight",
        props.className,
      )}
    >
      {props.children}
    </h3>
  );
}

export function TypographyH4(props: TypographyProps) {
  return (
    <h4
      className={cn(
        "scroll-m-20 text-xl font-semibold tracking-tight",
        props.className,
      )}
    >
      {props.children}
    </h4>
  );
}

export function TypographyP(props: TypographyProps) {
  return (
    <p className={cn("leading-7 not-first:mt-6", props.className)}>
      {props.children}
    </p>
  );
}

export function TypographyBlockquote(props: TypographyProps) {
  return (
    <blockquote className={cn("mt-6 border-l-2 pl-6 italic", props.className)}>
      {props.children}
    </blockquote>
  );
}

export function TypographyList(props: {
  children: React.ReactNode[];
  className?: string;
}) {
  return (
    <ul className={cn("my-6 ml-6 list-disc [&>li]:mt-2", props.className)}>
      {props.children.map((child, index) => (
        <li key={index}>{child}</li>
      ))}
    </ul>
  );
}

export function TypographyLead(props: TypographyProps) {
  return (
    <p className={cn("text-xl text-muted-foreground", props.className)}>
      {props.children}
    </p>
  );
}

export function TypographyMuted(props: TypographyProps) {
  return (
    <p className={cn("text-sm text-muted-foreground", props.className)}>
      {props.children}
    </p>
  );
}

export function TypographySmall(props: TypographyProps) {
  return (
    <small className={cn("text-sm font-medium leading-none", props.className)}>
      {props.children}
    </small>
  );
}

export function TypographyLarge(props: TypographyProps) {
  return (
    <div className={cn("text-lg font-semibold", props.className)}>
      {props.children}
    </div>
  );
}
